// Amazon Transcribe's framing, both ways — T57b (12 Sep 2026). AWS calls it event stream encoding:
// every chunk of audio the kadmon panel sends, and every transcript it gets back, is one message —
//
//   total length (4) · headers length (4) · prelude CRC (4) · headers · payload · message CRC (4)
//
// — big-endian, each CRC the gzip CRC32, the first over the prelude's eight bytes and the second
// over everything before it. Written out here rather than taken from the AWS SDK: it is a page of
// code, and the SDK's codec arrives with the SDK. The layout is AWS's, from "Setting up a streaming
// transcription" § Event stream encoding (docs.aws.amazon.com/transcribe/latest/dg/
// streaming-setting-up.html), and eventstream.test.ts decodes a frame AWS prints on that page,
// both checksums included.

/** One header's value, by the type byte AWS gives it (0–9). Transcribe sends strings. */
export type HeaderValue = boolean | number | bigint | string | Uint8Array | Date;

export interface EventMessage {
  headers: Record<string, HeaderValue>;
  payload: Uint8Array;
}

export class EventStreamError extends Error {
  override readonly name = 'EventStreamError';
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** CRC32 as gzip computes it, over `bytes[start, end)`. */
export function crc32(bytes: Uint8Array, start = 0, end = bytes.length): number {
  let crc = 0xffffffff;
  for (let i = start; i < end; i += 1) crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** Twelve bytes of prelude and its CRC, and four of message CRC. */
const OVERHEAD = 16;
const STRING = 7;

/** A message whose headers are strings — every header the brain sends is one. */
export function encodeMessage(
  headers: Readonly<Record<string, string>>,
  payload: Uint8Array,
): Uint8Array {
  const encoder = new TextEncoder();
  const encoded = Object.entries(headers).map(
    ([name, value]) => [encoder.encode(name), encoder.encode(value)] as const,
  );
  const headersLength = encoded.reduce(
    (sum, [name, value]) => sum + 4 + name.length + value.length,
    0,
  );
  const total = OVERHEAD + headersLength + payload.length;
  const message = new Uint8Array(total);
  const view = new DataView(message.buffer);
  view.setUint32(0, total);
  view.setUint32(4, headersLength);
  view.setUint32(8, crc32(message, 0, 8));
  let at = 12;
  for (const [name, value] of encoded) {
    message[at] = name.length;
    message.set(name, at + 1);
    at += 1 + name.length;
    message[at] = STRING;
    view.setUint16(at + 1, value.length);
    message.set(value, at + 3);
    at += 3 + value.length;
  }
  message.set(payload, at);
  at += payload.length;
  view.setUint32(at, crc32(message, 0, at));
  return message;
}

/** One message, both checksums checked; any header type AWS defines is read. */
export function decodeMessage(bytes: Uint8Array): EventMessage {
  if (bytes.length < OVERHEAD) {
    throw new EventStreamError(`a message is at least ${OVERHEAD} bytes, not ${bytes.length}`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const total = view.getUint32(0);
  const headersLength = view.getUint32(4);
  if (total !== bytes.length) {
    throw new EventStreamError(
      `the prelude says ${total} bytes and the frame holds ${bytes.length}`,
    );
  }
  if (view.getUint32(8) !== crc32(bytes, 0, 8)) {
    throw new EventStreamError('the prelude checksum does not match');
  }
  if (view.getUint32(total - 4) !== crc32(bytes, 0, total - 4)) {
    throw new EventStreamError('the message checksum does not match');
  }
  const end = 12 + headersLength;
  if (end > total - 4) throw new EventStreamError('the headers run past the message');

  const decoder = new TextDecoder();
  const headers: Record<string, HeaderValue> = {};
  let at = 12;
  while (at < end) {
    const nameLength = bytes[at]!;
    const name = decoder.decode(bytes.subarray(at + 1, at + 1 + nameLength));
    at += 1 + nameLength;
    const type = bytes[at]!;
    at += 1;
    switch (type) {
      case 0:
        headers[name] = true;
        break;
      case 1:
        headers[name] = false;
        break;
      case 2:
        headers[name] = view.getInt8(at);
        at += 1;
        break;
      case 3:
        headers[name] = view.getInt16(at);
        at += 2;
        break;
      case 4:
        headers[name] = view.getInt32(at);
        at += 4;
        break;
      case 5:
        headers[name] = view.getBigInt64(at);
        at += 8;
        break;
      case 6:
      case STRING: {
        const length = view.getUint16(at);
        const value = bytes.subarray(at + 2, at + 2 + length);
        headers[name] = type === STRING ? decoder.decode(value) : value.slice();
        at += 2 + length;
        break;
      }
      case 8:
        headers[name] = new Date(Number(view.getBigInt64(at)));
        at += 8;
        break;
      case 9:
        headers[name] = bytes.slice(at, at + 16);
        at += 16;
        break;
      default:
        throw new EventStreamError(`header ${name} has type ${type}, which AWS does not define`);
    }
  }
  if (at !== end) throw new EventStreamError('the headers do not end where the prelude says');
  return { headers, payload: bytes.slice(end, total - 4) };
}

/** One chunk of the recruiter's audio, as Transcribe takes it. An empty chunk ends the stream. */
export function audioEvent(pcm: Uint8Array): Uint8Array {
  return encodeMessage(
    {
      ':content-type': 'application/octet-stream',
      ':event-type': 'AudioEvent',
      ':message-type': 'event',
    },
    pcm,
  );
}
